import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faPlus, faBell } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { COMPLIANCE_NAV, SEVERITY_COLORS } from './nav';

interface Breach {
    id: number; title: string; description: string; severity: string; status: string;
    affected_record_estimate: number; discovered_at: string; notification_due_at: string | null;
    authority_notified_at: string | null; subjects_notified_at: string | null;
    containment_notes: string; notification_overdue: boolean; reported_by: string | null;
}

const STATUS_COLORS: Record<string, string> = {
    discovered: 'danger', contained: 'warning', notified: 'info', closed: 'success',
};
const STATUSES = ['discovered', 'contained', 'notified', 'closed'];

const BreachRegister: React.FC = () => {
    const { api } = useInternalApi();
    const [breaches, setBreaches] = useState<Breach[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ title: '', description: '', severity: 'medium', affected_record_estimate: 0 });
    const [viewing, setViewing] = useState<Breach | null>(null);
    const [notes, setNotes] = useState('');

    useEffect(() => { document.title = 'Breach Register - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('compliance-api/breaches/');
            if (res.data?.status === 'success') setBreaches(res.data.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const submitCreate = async () => {
        if (!form.title.trim() || !form.description.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Title and description are required.' });
            return;
        }
        const confirm = await Swal.fire({
            title: 'Record this breach?',
            text: 'A 72-hour authority notification deadline starts immediately on save.',
            icon: 'warning', showCancelButton: true, confirmButtonText: 'Record breach',
        });
        if (!confirm.isConfirmed) return;
        try {
            await api.post('compliance-api/breaches/', form);
            setShowCreate(false);
            setForm({ title: '', description: '', severity: 'medium', affected_record_estimate: 0 });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not record breach.' });
        }
    };

    const patchBreach = async (b: Breach, payload: any) => {
        try {
            const res = await api.patch(`compliance-api/breaches/${b.id}/`, payload);
            if (res.data?.status === 'success') {
                if (viewing?.id === b.id) setViewing(res.data.data);
                load();
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update breach.' });
        }
    };

    const overdue = breaches.filter(b => b.notification_overdue).length;

    return (
        <InternalShell activeDashboard="compliance_center" subNav={COMPLIANCE_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-primary" />Breach Register</h2>
                    <p className="text-muted mb-0">Incidents and the 72-hour notification workflow</p>
                </div>
                <button className="btn btn-danger" onClick={() => setShowCreate(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Record Breach</button>
            </div>

            {overdue > 0 && (
                <div className="alert alert-danger">
                    <FontAwesomeIcon icon={faBell} className="me-2" />
                    <strong>{overdue} incident(s) past the 72-hour authority notification deadline.</strong>
                </div>
            )}

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : breaches.length === 0 ? (
                        <div className="text-center py-5 text-muted">No breach incidents on record.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Incident</th><th>Severity</th><th>Status</th><th>Records</th><th>Discovered</th><th>Notify By</th><th></th></tr></thead>
                            <tbody>
                                {breaches.map(b => (
                                    <tr key={b.id} className={b.notification_overdue ? 'table-danger' : ''} style={{ cursor: 'pointer' }} onClick={() => { setViewing(b); setNotes(b.containment_notes); }}>
                                        <td>{b.title}</td>
                                        <td><span className={`badge bg-${SEVERITY_COLORS[b.severity]}`}>{b.severity}</span></td>
                                        <td><span className={`badge bg-${STATUS_COLORS[b.status]}`}>{b.status}</span></td>
                                        <td>{b.affected_record_estimate}</td>
                                        <td className="small">{new Date(b.discovered_at).toLocaleString()}</td>
                                        <td className="small">{b.notification_due_at ? new Date(b.notification_due_at).toLocaleString() : '—'}</td>
                                        <td>{b.authority_notified_at ? <span className="badge bg-success">Notified</span> : b.notification_overdue ? <span className="badge bg-danger">Overdue</span> : null}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Record Breach Incident</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Title</label>
                                    <input className="form-control" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">What happened</label>
                                    <textarea className="form-control" rows={4} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <label className="form-label">Severity</label>
                                        <select className="form-select" value={form.severity} onChange={(e) => setForm(f => ({ ...f, severity: e.target.value }))}>
                                            <option value="low">Low</option><option value="medium">Medium</option>
                                            <option value="high">High</option><option value="critical">Critical</option>
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Records affected (est.)</label>
                                        <input type="number" className="form-control" value={form.affected_record_estimate}
                                            onChange={(e) => setForm(f => ({ ...f, affected_record_estimate: Number(e.target.value) }))} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-danger" onClick={submitCreate}>Record Breach</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewing && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{viewing.title}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewing(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="d-flex gap-2 mb-3">
                                    <span className={`badge bg-${SEVERITY_COLORS[viewing.severity]}`}>{viewing.severity}</span>
                                    <span className={`badge bg-${STATUS_COLORS[viewing.status]}`}>{viewing.status}</span>
                                    <span className="badge bg-light text-dark">{viewing.affected_record_estimate} record(s)</span>
                                </div>
                                <p>{viewing.description}</p>

                                <div className="card bg-light mb-3"><div className="card-body py-2 small">
                                    <div>Discovered: {new Date(viewing.discovered_at).toLocaleString()}</div>
                                    <div>Authority notification due: {viewing.notification_due_at ? new Date(viewing.notification_due_at).toLocaleString() : '—'}</div>
                                    <div>Authority notified: {viewing.authority_notified_at ? new Date(viewing.authority_notified_at).toLocaleString() : <span className="text-danger">Not yet</span>}</div>
                                    <div>Data subjects notified: {viewing.subjects_notified_at ? new Date(viewing.subjects_notified_at).toLocaleString() : <span className="text-muted">Not yet</span>}</div>
                                </div></div>

                                <div className="mb-3">
                                    <label className="form-label small">Status</label>
                                    <select className="form-select form-select-sm" value={viewing.status} onChange={(e) => patchBreach(viewing, { status: e.target.value })}>
                                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>

                                <div className="d-flex gap-2 mb-3">
                                    {!viewing.authority_notified_at && (
                                        <button className="btn btn-sm btn-warning" onClick={() => patchBreach(viewing, { mark_authority_notified: true })}>
                                            Mark authority notified
                                        </button>
                                    )}
                                    {!viewing.subjects_notified_at && (
                                        <button className="btn btn-sm btn-outline-warning" onClick={() => patchBreach(viewing, { mark_subjects_notified: true })}>
                                            Mark data subjects notified
                                        </button>
                                    )}
                                </div>

                                <label className="form-label small">Containment notes</label>
                                <textarea className="form-control" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                                <button className="btn btn-primary" onClick={() => patchBreach(viewing, { containment_notes: notes })}>Save Notes</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default BreachRegister;
